import { DATE_FORMAT, Icon, IconButton, Tooltip } from '@dev-spendesk/grapes';

import { type GetV2ApiAuthenticationFactorsResponseOK } from 'src/client/spendesk-api-types';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

type AuthenticationFactorType =
  GetV2ApiAuthenticationFactorsResponseOK['data']['factors'][number]['type'];

type BaseProps = {
  id: string;
  name: string | null;
  lastUsedAt: string | null;
  type: AuthenticationFactorType;
};

type Props =
  | (BaseProps & {
      isLastAuthenticationFactor?: never;
      revocationFlow: 'disabled';
      handleRevokeFactor?: never;
    })
  | (BaseProps & {
      isLastAuthenticationFactor: boolean;
      revocationFlow: 'enabled';
      handleRevokeFactor: (factor: {
        factorId: string;
        factorName: string | null;
      }) => void;
    });

function getFactorIconName(type: AuthenticationFactorType) {
  switch (type) {
    case 'TOTP':
      return 'lock-close';
    case 'MOBILE_APP':
      return 'smartphone';
    case 'SMS':
      return 'chat-bubble-ellipsis';
    default:
      return 'circle-question-filled';
  }
}

function getDisplayName(type: AuthenticationFactorType, name: string | null) {
  switch (type) {
    case 'TOTP':
      return name as string;
    case 'MOBILE_APP':
      return 'Mobile App';
    case 'SMS':
      return 'SMS';
    default:
      return 'Unknown';
  }
}

export const AuthenticationFactorItem = ({
  id,
  name,
  lastUsedAt,
  type,
  isLastAuthenticationFactor,
  revocationFlow,
  handleRevokeFactor,
}: Props) => {
  const { t, localeFormat } = useTranslation('global');

  return (
    <div
      className="rounded-12 bg-background-secondary-default text-content-primary flex items-center p-16"
      key={id}
    >
      <Icon name={getFactorIconName(type)} size="l" />
      <div className="mr-24 ml-16 flex flex-1 flex-col gap-4">
        <span className="body-m">{getDisplayName(type, name)}</span>
        {lastUsedAt && (
          <span className="text-content-secondary-bg-secondary body-s">
            {t('common.authenticationFactor.lastUsed', {
              date: localeFormat(
                new Date(lastUsedAt),
                DATE_FORMAT.LONG_WITH_TIME,
              ),
            })}
          </span>
        )}
      </div>
      {revocationFlow === 'enabled' &&
        (isLastAuthenticationFactor ? (
          <Tooltip
            content={t('common.authenticationFactor.cannotDeleteLast')}
            placement="bottom"
          >
            <IconButton
              iconName="cross"
              variant="secondaryNeutral"
              aria-label={t('misc.delete')}
              disabled
              onClick={() => {
                handleRevokeFactor({ factorId: id, factorName: name });
              }}
            />
          </Tooltip>
        ) : (
          <IconButton
            iconName="cross"
            variant="secondaryNeutral"
            aria-label={t('misc.delete')}
            onClick={() => {
              handleRevokeFactor({ factorId: id, factorName: name });
            }}
          />
        ))}
    </div>
  );
};
